import React, { useEffect, useState } from "react";
import "./index.scss";
import { Menu, MenuItem, IconButton, Tooltip, Badge } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import { chatPaneToggle } from "../../store/actions/chatbot";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Translate } from "react-auto-translate";
import { authenticationLogout } from "../../store/actions/authentication";
import { getUser } from "../../store/actions/user";
import Notification from "../../components/Notification";
import BranchSelect from "./BranchSelect";
import MyBranchSwitcher from "./MyBranchSwitcher";
import SelectLanguage from "./SelectLanguage";
import { openReleaseNotes, getReleaseNotes } from "../../store/actions/releaseNotes";
import { LoadingState } from "../../store/reducers/types";
import settingIcon from "../../cdn/settingLogo.svg";
import logoutIcon from "../../cdn/LogoutLogo.svg";
import userLogo from "../../cdn/userLogo.svg";
import ayesalonLogo from "../../cdn/ayesalonHeader.png";

const DEFAULT_BRANCH_LOGO = "https://s3.ap-south-1.amazonaws.com/ayesalon-partner-logo.bkp/ayesalon.jpeg";
import ToastAlert from "../../utilities/toastAlert/toastAlert";

function Header({ refresh, pathMap }) {
  const history = useHistory();
  const dispatch = useDispatch();

  const [isDarkTheme, setDarkTheme] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const user = useSelector((state) => state.user);
  const chatPaneOpen = useSelector((state) => state.chatbot.chatPaneOpen);
  const chatbotAccess = useSelector((state) => state.chatbot.access);
  const branch = useSelector((state) => state.branch.data);
  const { base_permissions } = useSelector((state) => state.aclUserPermissions.data);
  const releaseNotes = useSelector((state) => state.releaseNotes);

  const settingPageAccessible =
    (base_permissions || []).filter((permission) => permission.permission_name === "settings_page").length > 0;

  useEffect(() => {
    dispatch(getUser());
    const currentTheme = localStorage.getItem("theme") ? localStorage.getItem("theme") : "light";
    if (currentTheme === "dark") {
      setDarkTheme(true);
    }
    // Fetch once on load so the unread badge is correct before the user ever
    // opens the modal (which otherwise only fetches once it's opened).
    dispatch(getReleaseNotes({}));
  }, []);

  useEffect(() => {
    if (Object.keys(pathMap).length === 0) {
      return;
    }
    //for redirecting the user(especially admin) to the first router
    if (!("/" in pathMap)) {
      history.replace(Object.keys(pathMap)[0]);
    }
  }, [pathMap]);

  const { logo, name } = branch || {};
  const navigateTo = () => history.push("/settings");
  const logout = () => dispatch(authenticationLogout());

  const themeSwitch = ({ checked }) => {
    if (checked) {
      document.documentElement.setAttribute("data-theme", "dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.setAttribute("data-theme", "light");
      localStorage.setItem("theme", "light");
    }
    setDarkTheme(checked);
  };

  return (
    <div className="header">
      {/* Left: hamburger (mobile) + salon name */}
      <div className="left">
        <div className="hamburger">
          <IconButton
            className="pointer"
            onClick={() => {
              document.getElementById("side-bar").style.width = "150px";
              document.getElementById("mobile_quit").style.display = "block";
            }}
          >
            <MenuIcon />
          </IconButton>
        </div>
        <span className="brand-plus"><img src={ayesalonLogo} alt="Ayesalon" /></span>
      </div>

      {/* Center: salon name + optional branch partner logo */}
      <div className="center branch-details">
        <span className="brand-center">{name || "Ayesalon"}</span>
        <img className="partner-logo" src={logo || DEFAULT_BRANCH_LOGO} alt="" />
      </div>

      {/* Right: icon row + language + notifications + user menu */}
      <div className="right">
        <BranchSelect />
        <MyBranchSwitcher />
        <SelectLanguage className="headerLang" />
        <Notification className="headerNotification" />
        {!branch?.is_freemium && (
        <Tooltip title="Release Notes">
          <IconButton size="small" onClick={() => dispatch(openReleaseNotes())}>
            <Badge
              variant="dot"
              color="error"
              invisible={releaseNotes.loading !== LoadingState.loaded || releaseNotes.read}
            >
              <NewReleasesIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>
        )}
        {chatbotAccess.allowed !== null && chatbotAccess.reason !== "disabled" && (
          <Tooltip title="AI Assistant">
            <IconButton
              size="small"
              onClick={() => {
                const allowed = sessionStorage.getItem("chatbot_allowed");
                const reason = sessionStorage.getItem("chatbot_block_reason");
                if (allowed === "0") {
                  const msg =
                    reason === "trial"
                      ? "The Aye Assistant is a paid feature. Please upgrade your subscription to access it."
                      : "Your subscription has expired. Please renew to continue using the Aye Assistant.";
                  ToastAlert({ message: msg, type: "warning", autoClose: 4000 });
                  if (typeof window.gtag === "function") {
                    window.gtag("event", "chatbot_open_blocked", { reason });
                  }
                  return;
                }
                if (typeof window.gtag === "function") {
                  window.gtag("event", "chatbot_toggle", { action: chatPaneOpen ? "close" : "open" });
                }
                dispatch(chatPaneToggle());
              }}
              className={chatPaneOpen ? "chatbot-nav-icon--active" : ""}
            >
              <SmartToyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <ul className="right-menu">
          <li>
            <div className="header-menu">
              <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} size="small">
                <img src={userLogo} alt="user" />
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                className="userSetting"
              >
                {settingPageAccessible && (
                  <MenuItem
                    key={1}
                    onClick={() => {
                      setUserMenuAnchor(null);
                      navigateTo();
                    }}
                  >
                    <Translate>
                      <img src={settingIcon} alt="setting Icon" />
                      Settings
                    </Translate>
                  </MenuItem>
                )}
                <MenuItem
                  key={2}
                  onClick={() => {
                    setUserMenuAnchor(null);
                    if (typeof window.gtag === "function") {
                      window.gtag("event", "logout");
                    }
                    logout();
                  }}
                >
                  <Translate>
                    <img src={logoutIcon} alt="logout Icon" />
                    Logout
                  </Translate>
                </MenuItem>
              </Menu>
            </div>
          </li>
        </ul>
      </div>
    </div>
  );
}
export default Header;
